import keyBy from 'lodash/keyBy';
import {
	createContainer,
	createHook,
	createStore,
	type StoreActionApi,
	createSelector,
	createStateHook,
	createActionsHook,
} from 'react-sweet-state';
import type {
	Transition,
	WorkflowOld,
	Column,
} from '@atlassian/jira-business-board-workflow-issues';
import type {
	BusinessIssueField,
	BusinessIssueType,
} from '@atlassian/jira-business-entity-project';
import type { WorkflowDetails } from '@atlassian/jira-business-workflows';
import { ff } from '@atlassian/jira-feature-flagging';
import { CATEGORY_TYPE } from '@atlassian/jira-platform-field-config';
import type { ExtendedFields, BoardColumn, ColumnId, Status } from '../../common/types';
import {
	getActiveWorkflowDetails,
	getColumnByStatusId,
	getInitialTransition,
	getIssueById,
	getTransitionById,
	getTransitionsByStatus,
	getWorkflowDetailsWithoutSubtasksOnlyWorklows,
	hasMultipleTransitions,
	hasMultipleWorkflows,
	isGroupByFieldAllowed,
} from './selectors';
import type {
	BoardColumnToDelete,
	BoardState,
	CardDragState,
	Issue,
	IssuesWithFields,
	BoardIssueFieldUpdate,
} from './types';
import { updateExtendedFields } from './utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const emptyObj: Record<string, any> = {};
const emptyArray: Array<Column> = [];
const emptyTransitions: Array<Transition> = [];
const emptyIssues: Array<Issue> = [];
const emptyWorkflows: WorkflowOld[] = [];
const emptyWorkflowsDetails: WorkflowDetails[] = [];
const emptyFieldIds: Array<string> = [];

const initialState: BoardState = {
	workflowName: null,
	workflowId: null,
	issues: emptyIssues,
	people: emptyObj,
	transitions: emptyTransitions,
	columns: emptyArray,
	loading: true,
	error: null,
	cardDragState: {
		issue: null,
		hoverIntent: false,
		transitionId: null,
		columnId: null,
	},
	taskQueue: null,
	workflows: emptyWorkflows,
	workflowsDetails: emptyWorkflowsDetails,
	columnToDelete: null,
	columnToDeleteHover: null,
	isDeleteColumnInProgress: false,
	subtaskParentMap: {},
};
/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const setIssueTypes =
	(issueTypes: BusinessIssueType[]) =>
	({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		setState({
			issueTypes: keyBy(issueTypes, 'id'),
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const setIssueFields =
	(issueFields: BusinessIssueField[]) =>
	({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const categoryFieldId = issueFields.find((field) => field.type === CATEGORY_TYPE)?.id;
		// For now, we only need category field id to load extended fields but other fields are also stored for
		// forwards compatibility.
		if (categoryFieldId == null) {
			setState({
				issueFields: keyBy(issueFields, 'id'),
			});
		} else {
			setState({
				issueFields: keyBy(issueFields, 'id'),
				categoryFieldId,
			});
		}
	};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
const pushToJobQueue =
	(job: () => Promise<void>) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		const { taskQueue } = getState();
		if (taskQueue === null) {
			setState({
				loading: true,
			});
			const jobPromise = job();
			const newPromise = Promise.allSettled([jobPromise]).then(() => {
				setState({ taskQueue: null, loading: false });
				return jobPromise;
			});
			setState({
				taskQueue: newPromise,
			});
			return newPromise;
		}
		const aggregatedPromise = Promise.allSettled([taskQueue]).then(() => {
			setState({
				loading: true,
			});
			const jobPromise = job();
			return Promise.allSettled([jobPromise]).then(() => {
				setState({ taskQueue: null, loading: false });
				return jobPromise;
			});
		});
		setState({
			taskQueue: aggregatedPromise,
		});
		return aggregatedPromise;
	};

const addColumn =
	(column: Column & { loading: boolean }) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		const { columns, workflows } = getState();
		setState({
			columns: [...columns, column],
			workflows: workflows.map((wf) => ({
				...wf,
				columns: [...wf.columns, column],
			})),
		});
	};

const setColumns =
	(columns: Column[]) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		const { workflowId, workflows } = getState();
		setState({
			columns,
			workflows: workflows.map((workflow) =>
				workflow.workflowId === workflowId ? { ...workflow, columns } : workflow,
			),
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const setIssues =
	(issues: Issue[]) =>
	({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		setState({
			issues,
		});
	};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
const setColumnToDelete =
	(columnToDelete: BoardColumnToDelete) =>
	({ setState }: StoreActionApi<BoardState>) => {
		setState({
			columnToDelete,
		});
	};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
const setColumnToDeleteHover =
	(columnToDeleteHover: BoardColumnToDelete) =>
	({ setState }: StoreActionApi<BoardState>) => {
		setState({
			columnToDeleteHover,
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const moveIssues =
	(
		issueIDs: number[],
		sourceColumnId: ColumnId,
		targetColumnId: ColumnId,
		relativeIssueId: number | null,
		shouldRankAfter: boolean,
		optimisticUpdates?: Partial<Issue> | BoardIssueFieldUpdate[],
	) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const state = getState();
		const { issues } = state;
		const issueById = getIssueById(state);
		let destinationIndex =
			relativeIssueId === null ? 0 : issues.findIndex((i) => i.id === relativeIssueId);
		if (shouldRankAfter && sourceColumnId !== targetColumnId) {
			destinationIndex += 1;
		}

		const updatedIssues = [...issues];
		[...issueIDs].reverse().forEach((issueId) => {
			const issue = issueById(issueId);
			const issueIndex = updatedIssues.findIndex((i) => i.id === issue?.id);
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			let updatedIssue = { ...issue, statusId: targetColumnId as number };
			if (optimisticUpdates != null) {
				if (Array.isArray(optimisticUpdates)) {
					// Extended field updates
					updatedIssue = {
						...issue,
					};
					const issueDetails = state.issuesWithFields?.[issueId];
					if (issueDetails != null) {
						// Issue details are transformed generic type, optimistic update generic type
						const { issuesWithFields: currentIssuesWithFields } = getState();
						setState({
							issuesWithFields: {
								...currentIssuesWithFields,
								...{
									[String(issueId)]: updateExtendedFields(
										issueDetails,
										// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
										optimisticUpdates as BoardIssueFieldUpdate[],
									),
								},
							},
						});
					}
				} else {
					// Issue field updates
					updatedIssue = {
						...issue,
						...optimisticUpdates,
					};
				}
			}
			updatedIssues.splice(issueIndex, 1);
			const newIndex =
				issueIndex < destinationIndex && sourceColumnId !== targetColumnId
					? destinationIndex - 1
					: destinationIndex;
			updatedIssues.splice(newIndex, 0, updatedIssue);
		});
		setState({
			issues: updatedIssues,
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const addIssue =
	(issue: Issue, extendedFields?: ExtendedFields, rankBeforeIssueId?: number) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issues } = getState();
		let updatedIssues: Issue[];

		if (rankBeforeIssueId != null) {
			const issueIndex = issues.findIndex((i) => i.id === rankBeforeIssueId);
			updatedIssues = [...issues];
			updatedIssues.splice(issueIndex, 0, issue);
		} else {
			updatedIssues = [...issues, issue];
		}

		if (extendedFields == null) {
			setState({
				issues: updatedIssues,
			});
		} else {
			const { issuesWithFields: currentIssuesWithFields } = getState();
			const issueId = issue.id;
			setState({
				issues: updatedIssues,
				issuesWithFields: {
					...currentIssuesWithFields,
					...{ [String(issueId)]: extendedFields },
				},
			});
		}
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const removeIssue =
	(issueId: number) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issues } = getState();
		setState({
			issues: [...issues.filter((i) => i.id !== issueId)],
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const setCardDragState =
	(cardDragState: CardDragState) =>
	({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		setState({ cardDragState });
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const updateIssue =
	(issueId: number, updates: Partial<Issue>, extendedFields?: ExtendedFields) =>
	({ getState, setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issues, issuesWithFields: currentIssuesWithFields, subtaskParentMap } = getState();

		const existingIssue = issues.find((issue) => issue.id === issueId);

		if (!existingIssue) {
			return;
		}

		const updatedIssues = issues.map((issue) =>
			issue.id === issueId ? { ...issue, ...updates } : issue,
		);
		if (updates.subtasks != null) {
			updates.subtasks.forEach((subtaskIssue) => {
				subtaskParentMap[subtaskIssue.id] = issueId;
			});
		}

		const newState: Partial<BoardState> = {
			issues: updatedIssues,
			subtaskParentMap,
		};

		if (extendedFields) {
			const updatedIssueId = String(updates.id ?? issueId);
			const hasUpdatedIssueId = updates.id && existingIssue.id !== updates.id;

			const updatedIssuesWithFields: BoardState['issuesWithFields'] = {
				...currentIssuesWithFields,
				[String(updatedIssueId)]: extendedFields,
			};

			/*
			 * If the issue ID has changed, remove the old issue ID from the issuesWithFields map.
			 * This ensures that the issue IDs referenced in the map match the issue IDs in the issues array.
			 */
			if (hasUpdatedIssueId) {
				delete updatedIssuesWithFields[issueId];
			}

			newState.issuesWithFields = updatedIssuesWithFields;
		}

		setState(newState);
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const clearStoredFieldIds =
	() =>
	({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		setState({
			issueDetailFields: emptyFieldIds,
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const updateIssuesWithFields =
	(updatedIssuesWithFields: IssuesWithFields, fields: string[]) =>
	({ getState, setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issuesWithFields: currentIssuesWithFields, issues } = getState();
		setState({
			issuesWithFields: {
				...currentIssuesWithFields,
				...updatedIssuesWithFields,
			},
			issueDetailFields: fields,
			// Clear needsExtendedFieldsUpdate flag from all issues
			issues: issues.map(({ needsExtendedFieldsUpdate, ...issue }) => issue),
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const removeIssueWithFields =
	(issueId: number) =>
	({ getState, setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issuesWithFields } = getState();
		if (issuesWithFields != null) {
			delete issuesWithFields[issueId];
			Object.keys(issuesWithFields).forEach((id) => {
				const issueDetails = issuesWithFields[Number(id)];
				if (issueDetails?.fieldsByAlias?.parent?.parent?.id === String(issueId)) {
					delete issuesWithFields[Number(id)];
				}
			});
		}
		setState({
			issuesWithFields,
		});
	};

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
const updateConfluenceLinksCount =
	(issueId: number, count: number) =>
	({ getState, setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			throw new Error('Project BAE is enabled');
		}

		const { issuesWithFields: currentIssuesWithFields } = getState();

		const currentIssueDetails = currentIssuesWithFields?.[issueId];
		if (currentIssueDetails?.fieldsByAlias?.confluenceLinksCount?.value == null) {
			return;
		}

		const updatedIssueDetails = {
			...currentIssueDetails,
			fieldsByAlias: {
				...currentIssueDetails.fieldsByAlias,
				confluenceLinksCount: {
					...currentIssueDetails.fieldsByAlias.confluenceLinksCount,
					value: count,
				},
			},
		};

		setState({
			issuesWithFields: {
				...currentIssuesWithFields,
				...{ [issueId]: updatedIssueDetails },
			},
		});
	};

const setWorkflowsDetails =
	(workflowsDetails: WorkflowDetails[]) =>
	({ setState }: StoreActionApi<BoardState>) => {
		setState({ workflowsDetails });
	};

/**
 * Used to modify a specific column based on statusId with partial data of Column
 */
const modifyColumn =
	(statusId: number, columnData: Partial<Column> & { loading: boolean }) =>
	({ getState, setState }: StoreActionApi<BoardState>) => {
		const { columns, workflows } = getState();

		const modifyChosenColumns = (columnsToModify: Column[]): Column[] => {
			const selectedColumnIndex = columnsToModify.findIndex((c) => c.statusId === statusId);
			if (selectedColumnIndex === -1) {
				return columnsToModify;
			}

			const updatedColumns = [...columnsToModify];
			const [sourceColumn] = updatedColumns.splice(selectedColumnIndex, 1);
			updatedColumns.splice(selectedColumnIndex, 0, {
				...sourceColumn,
				...columnData,
			});

			return updatedColumns;
		};

		const updatedColumns = modifyChosenColumns(columns);
		const updatedWorkflows = workflows.map((wf) => {
			const wfUpdatedColumns = modifyChosenColumns(wf.columns);

			return {
				...wf,
				columns: wfUpdatedColumns,
			};
		});

		setState({
			columns: updatedColumns,
			workflows: updatedWorkflows,
		});
	};

const deleteColumn =
	(
		columnToDelete: BoardColumn,
		toStatus: Status,
		columnData: Partial<Column> & { loading: boolean },
	) =>
	({ setState, getState }: StoreActionApi<BoardState>) => {
		const { columns, workflows } = getState();
		const toColumnData = columnData;

		if (columnToDelete.isDone) {
			toColumnData.statusCategory = columnToDelete.statusCategory;
		}

		const getUpdatedColumns = (columnsToUpdate: Array<Column>) => {
			const updatedColumns = [...columnsToUpdate];
			const columnToDeleteIndex = columnsToUpdate.findIndex(
				(c) => c.statusId === columnToDelete.statusId,
			);
			updatedColumns.splice(columnToDeleteIndex, 1);
			return updatedColumns;
		};

		const updatedColumns = getUpdatedColumns(columns);
		const toColumnIndex = updatedColumns.findIndex((c) => c.name === toStatus.name);
		const [toColumn] = updatedColumns.splice(toColumnIndex, 1);
		updatedColumns.splice(toColumnIndex, 0, {
			...toColumn,
			...toColumnData,
		});
		const updatedWorkflows = workflows.map((wf) => ({
			...wf,
			columns: getUpdatedColumns(wf.columns),
		}));
		setState({
			columns: updatedColumns,
			workflows: updatedWorkflows,
			columnToDelete: null,
			isDeleteColumnInProgress: true,
		});
	};

const setColumnsAndWorkflows =
	(columns: Column[], workflows: WorkflowOld[]) =>
	({ setState }: StoreActionApi<BoardState>) => {
		setState({ columns, workflows, isDeleteColumnInProgress: false });
	};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
const setDeleteColumnInProgress =
	(isDeleteColumnInProgress: boolean) =>
	({ setState }: StoreActionApi<BoardState>) => {
		setState({ isDeleteColumnInProgress });
	};

const setFetchResponse =
	({
		workflows,
		activeWorkflow,
		workflowIssuesLastChangedTime,
	}: {
		workflows: WorkflowOld[];
		activeWorkflow: WorkflowOld;
		workflowIssuesLastChangedTime?: string;
	}) =>
	async ({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			setState({
				columns: activeWorkflow.columns,
				transitions: activeWorkflow.transitions,
				workflowId: activeWorkflow.workflowId,
				workflowName: activeWorkflow.workflowName,
				workflows,
				workflowIssuesLastChangedTime,
				loading: false,
			});
		} else {
			setState({
				...activeWorkflow,
				workflows,
				workflowIssuesLastChangedTime,
				loading: false,
			});
		}
	};

const setWorkflowData =
	(workflow: WorkflowOld) =>
	async ({ setState }: StoreActionApi<BoardState>) => {
		if (ff('jwm.board-awesome-enhancements')) {
			setState({
				columns: workflow.columns,
				transitions: workflow.transitions,
				workflowId: workflow.workflowId,
				workflowName: workflow.workflowName,
			});
		} else {
			setState({
				...workflow,
			});
		}
	};

const actions = {
	addColumn,
	setColumns,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setIssues,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	moveIssues,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setCardDragState,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	addIssue,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	removeIssue,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	updateIssue,
	modifyColumn,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	pushToJobQueue,
	setWorkflowsDetails,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setColumnToDelete,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setColumnToDeleteHover,
	deleteColumn,
	setColumnsAndWorkflows,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setDeleteColumnInProgress,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	updateIssuesWithFields,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	updateConfluenceLinksCount,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	removeIssueWithFields,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	clearStoredFieldIds,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setIssueTypes,
	/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
	setIssueFields,
	setFetchResponse,
	setWorkflowData,
};

export const BoardStoreContainer = createContainer();

export const BoardStore = createStore({
	initialState,
	actions,
	name: 'BoardStore',
	containedBy: BoardStoreContainer,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useTransitionsByStatus = createHook(BoardStore, {
	selector: getTransitionsByStatus,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useTransitionById = createHook(BoardStore, {
	selector: getTransitionById,
});

/**
 * Returns true if
 *  there are any statuses such as S1 and S2 with multiple transitions from S1 to S2 or
 *  there's a status S3 with a transition from S3 to S3(self transition).
 */
/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useHasMultipleTransitionsBetweenStatuses = createHook<
	BoardState,
	typeof actions,
	boolean
>(BoardStore, {
	selector: hasMultipleTransitions,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useColumnByStatusId = createHook(BoardStore, {
	selector: getColumnByStatusId,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useGetIssueById = createHook(BoardStore, {
	selector: getIssueById,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useInitialTransition = createHook<
	BoardState,
	typeof actions,
	ReturnType<typeof getInitialTransition>
>(BoardStore, {
	selector: getInitialTransition,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useBoardStore = createHook(BoardStore);

export const useBoardStoreState = createStateHook(BoardStore);

export const useBoardStoreActions = createActionsHook(BoardStore);

export const useHasMultipleWorkflows = createStateHook<
	BoardState,
	typeof actions,
	ReturnType<typeof hasMultipleWorkflows>
>(BoardStore, {
	selector: hasMultipleWorkflows,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useActiveWorkflowDetails = createHook<
	BoardState,
	typeof actions,
	ReturnType<typeof getActiveWorkflowDetails>
>(BoardStore, {
	selector: getActiveWorkflowDetails,
});

export const useWorkflowDetailsWithoutSubtasksOnlyWorkflows = createHook<
	BoardState,
	typeof actions,
	ReturnType<typeof getWorkflowDetailsWithoutSubtasksOnlyWorklows>
>(BoardStore, {
	selector: getWorkflowDetailsWithoutSubtasksOnlyWorklows,
});

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
const areAnyColumnsUpdating = createSelector<BoardState, void, boolean, Column[]>(
	(state: BoardState) => state.columns,
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	(columns) => columns.some((column) => (column as any)?.loading),
);

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useAreAnyColumnsUpdating = createHook(BoardStore, {
	selector: areAnyColumnsUpdating,
});

/** @deprecated Do not use in new board implementation (jwm.board-awesome-enhancements) */
export const useIsGroupByFieldAllowed = createHook(BoardStore, { selector: isGroupByFieldAllowed });
